import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatCurrency, US_STATES } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useEntity } from "../lib/entity";
import Layout from "../components/Layout";
import HelpTip from "../components/Help/HelpTip";
import EmptyState from "../components/Help/EmptyState";
import { PropertiesSkeleton, DataErrorState } from "../components/Skeleton/SkeletonComponents";
import { Plus, Building2, MapPin, Layers, Percent, Calendar, DollarSign, Upload, Camera } from "lucide-react";

import { toast } from "sonner";


const EMPTY = {
  name: "", address: "", city: "", state: "", zip: "",
  purchase_date: "", sqft: "", purchase_price: "", mortgage_amount: "",
  mortgage_balance: "", monthly_mortgage_payment: "", leasing_type: "LTR",
  type: "LTR", nightly_rate: "", cleaning_fee: "", platform_fee_pct: "",
  monthly_rent: "", security_deposit: "", personal_use_days: "",
  lease_start_date: "", lease_end_date: "", image_url: "",
  entity_id: "", interest_rate: 6.5, loan_term_years: 30, origination_date: "",
};


export default function Properties() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const { activeEntityId, entities } = useEntity();

  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Photo exceeds 15MB limit");
      return;
    }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/properties/upload-photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((prev) => ({ ...prev, image_url: res.data.image_url }));
      toast.success("Cover photo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Photo upload failed");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const startTime = Date.now();
    try {
      const q = activeEntityId !== "all" ? { entity_id: activeEntityId } : {};
      const res = await api.get("/properties", { params: q });
      setItems(res.data || []);
    } catch (err) {
      console.error("Failed to load properties:", err);
      setLoadError(err.response?.data?.detail || "Failed to load properties");
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 300 - elapsed);
      setTimeout(() => {
        setLoading(false);
      }, remaining);
    }
  }, [activeEntityId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (params.get("new")) {
      setShowForm(true);
      setParams({});
    }
  }, [params, setParams]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      payload.type = payload.leasing_type;
      payload.mortgage_balance = parseFloat(payload.mortgage_amount) || 0;
      payload.sq_ft = parseFloat(payload.sqft) || 0;
      payload.entity_id = payload.entity_id || (entities[0]?.id || null);
      payload.interest_rate = parseFloat(payload.interest_rate) || 0;
      payload.loan_term_years = parseInt(payload.loan_term_years) || 30;

      ["purchase_price","mortgage_amount","mortgage_balance","monthly_mortgage_payment",
       "nightly_rate","cleaning_fee","platform_fee_pct","monthly_rent","security_deposit",
       "sqft","sq_ft","personal_use_days"]
        .forEach(k => payload[k] = parseFloat(payload[k]) || 0);

      payload.lease_start_date = payload.lease_start_date || null;
      payload.lease_end_date = payload.lease_end_date || null;
      payload.purchase_date = payload.purchase_date || null;
      payload.origination_date = payload.origination_date || payload.purchase_date || null;

      await api.post("/properties", payload);
      toast.success("Property added to portfolio");
      setShowForm(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add property");
    }
  };

  const getInitials = (name) => {
    if (!name) return "LP";
    const parts = name.trim().split(" ");
    return parts.length >= 2 ? (parts[0][0] + parts[1][0]).toUpperCase() : name.slice(0, 2).toUpperCase();
  };

  return (
    <Layout
      title="Properties"
      subtitle={loading ? "Loading portfolio assets..." : `${items.length} real estate assets in portfolio`}
      actions={
        can.edit && (
          <button
            onClick={() => setShowForm(true)}
            data-testid="new-property-button"
            className="btn-primary text-xs sm:text-sm py-1.5 sm:py-2 px-3 sm:px-4"
          >
            <Plus size={15} />
            <span>New Property</span>
          </button>

        )
      }
    >
      {loading ? (
        <PropertiesSkeleton />
      ) : loadError ? (
        <DataErrorState message={loadError} onRetry={load} />
      ) : (
        <>
          {showForm && (
        <div className="card p-4 sm:p-6 mb-6 shadow-xl border border-[var(--border)] rounded-2xl animate-in fade-in duration-200" data-testid="property-form">
          <div className="flex items-center justify-between border-b border-[var(--border)] pb-4 mb-4">
            <h3 className="text-lg font-bold">Add Property to Portfolio</h3>
            <button onClick={() => setShowForm(false)} className="text-xs text-[var(--muted)] font-semibold hover:text-[var(--text)]">
              Cancel
            </button>
          </div>

          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Owning LLC Entity</label>
              <select
                value={form.entity_id}
                onChange={(e) => setForm({ ...form, entity_id: e.target.value })}
                className="lapen-input"
              >
                <option value="">Select LLC Entity...</option>
                {entities.map((ent) => (
                  <option key={ent.id} value={ent.id}>{ent.name}</option>
                ))}
              </select>
            </div>

            <Field label="Property Name *" value={form.name} onChange={(v) => setForm({...form, name: v})} required testId="prop-name" />
            <Field label="Physical Address *" value={form.address} onChange={(v) => setForm({...form, address: v})} required testId="prop-address" />

            <div className="grid grid-cols-3 gap-2">
              <Field label="City" value={form.city} onChange={(v) => setForm({...form, city: v})} testId="prop-city" />
              <div>
                <label className="label">State</label>
                <select
                  className="lapen-input"
                  value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value })}
                  data-testid="prop-state"
                >
                  <option value="">Select State...</option>
                  {US_STATES.map((st) => (
                    <option key={st.code} value={st.code}>
                      {st.code} — {st.name}
                    </option>
                  ))}
                </select>
              </div>
              <Field label="Zip" value={form.zip} onChange={(v) => setForm({...form, zip: v})} testId="prop-zip" />
            </div>


            <SelectField label="Leasing Strategy" value={form.leasing_type} onChange={(v) => setForm({...form, leasing_type: v, type: v})} options={["LTR","STR","Hybrid"]} testId="prop-leasing-type" helpTopic="leasing-type" />
            <Field label="Living Area (Sq Ft)" type="number" value={form.sqft} onChange={(v) => setForm({...form, sqft: v, sq_ft: v})} testId="prop-sqft" />
            <Field label="Purchase Date" type="date" value={form.purchase_date} onChange={(v) => setForm({...form, purchase_date: v})} testId="prop-purchase-date" />
            <Field label="Purchase Price ($)" type="number" value={form.purchase_price} onChange={(v) => setForm({...form, purchase_price: v})} testId="prop-purchase" helpTopic="purchase-price" />

            {/* Mortgage fields */}
            <Field label="Mortgage Loan Balance ($)" type="number" value={form.mortgage_amount} onChange={(v) => setForm({...form, mortgage_amount: v, mortgage_balance: v})} testId="prop-mortgage" helpTopic="mortgage-balance" />
            <Field label="Monthly P&I Payment ($)" type="number" value={form.monthly_mortgage_payment} onChange={(v) => setForm({...form, monthly_mortgage_payment: v})} testId="prop-mortgage-pmt" helpTopic="mortgage-tracker" />


            <div className="grid grid-cols-2 gap-2">
              <Field label="Interest Rate (%)" type="number" value={form.interest_rate} onChange={(v) => setForm({...form, interest_rate: v})} />
              <Field label="Loan Term (Years)" type="number" value={form.loan_term_years} onChange={(v) => setForm({...form, loan_term_years: v})} />
            </div>

            <div className="sm:col-span-2">
              <label className="label">Cover Photo</label>
              {form.image_url ? (
                <div className="relative rounded-2xl overflow-hidden border border-[var(--border)] aspect-[21/9] sm:aspect-[24/8] max-h-48 group">
                  <img src={form.image_url} alt="Cover Preview" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                    <button
                      type="button"
                      onClick={() => photoInputRef.current?.click()}
                      className="btn-secondary text-xs py-1.5 px-3 backdrop-blur-md"
                    >
                      Change Photo
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, image_url: "" })}
                      className="p-1.5 px-3 rounded-lg bg-red-500/80 hover:bg-red-500 text-white text-xs font-semibold backdrop-blur-md transition-colors"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      type="button"
                      onClick={() => photoInputRef.current?.click()}
                      disabled={uploadingPhoto}
                      className="btn-primary text-xs py-2 px-4 flex items-center justify-center gap-1.5 shrink-0"
                    >
                      <Upload size={14} />
                      <span>{uploadingPhoto ? "Uploading..." : "Upload Cover Photo"}</span>
                    </button>
                    <input
                      type="file"
                      ref={photoInputRef}
                      onChange={handlePhotoUpload}
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                    />
                    <input
                      type="text"
                      placeholder="Or paste image URL (https://...)"
                      value={form.image_url}
                      onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                      className="lapen-input text-xs flex-1"
                      data-testid="prop-image"
                    />
                  </div>
                  <p className="text-[11px] text-[var(--muted)]">
                    Upload a property photo (JPG, PNG, WEBP up to 15MB) or paste an image URL.
                  </p>
                </div>
              )}
            </div>


            {(form.leasing_type === "STR" || form.leasing_type === "Hybrid") && (
              <>
                <Field label="Nightly Rate ($)" type="number" value={form.nightly_rate} onChange={(v) => setForm({...form, nightly_rate: v})} testId="prop-nightly" />
                <Field label="Cleaning Fee ($)" type="number" value={form.cleaning_fee} onChange={(v) => setForm({...form, cleaning_fee: v})} testId="prop-cleaning" />
                <Field label="Platform Fee %" type="number" value={form.platform_fee_pct} onChange={(v) => setForm({...form, platform_fee_pct: v})} testId="prop-platform" />
                <Field label="Personal Use Days (IRS § 280A)" type="number" value={form.personal_use_days} onChange={(v) => setForm({...form, personal_use_days: v})} testId="prop-personal-days" />
              </>
            )}

            {(form.leasing_type === "LTR" || form.leasing_type === "Hybrid") && (
              <>
                <Field label="Monthly Rent ($)" type="number" value={form.monthly_rent} onChange={(v) => setForm({...form, monthly_rent: v})} testId="prop-rent" />
                <Field label="Security Deposit ($)" type="number" value={form.security_deposit} onChange={(v) => setForm({...form, security_deposit: v})} testId="prop-deposit" />
                <Field label="Lease Start" type="date" value={form.lease_start_date} onChange={(v) => setForm({...form, lease_start_date: v})} testId="prop-lease-start" />
                <Field label="Lease End" type="date" value={form.lease_end_date} onChange={(v) => setForm({...form, lease_end_date: v})} testId="prop-lease-end" />
              </>
            )}

            <div className="sm:col-span-2 flex justify-end gap-3 mt-4 pt-4 border-t border-[var(--border)]">
              <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Cancel</button>
              <button type="submit" data-testid="prop-submit" className="btn-primary">Save Property</button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="properties-list">
        {items.length === 0 && !showForm && (
          <div className="col-span-full">
            <EmptyState
              icon={Building2}
              title="No Properties Added Yet"
              description="Add your first rental property to track valuations, cash flow, debt amortization, and IRS tax depreciation."
              helpTopic="leasing-type"
              helpText="Learn about leasing strategies & IRS rules"
              actionLabel="+ Add Your First Property"
              onAction={() => setShowForm(true)}
              testId="properties-empty-state"
            />
          </div>
        )}


        {items.map((p) => {
          const badgeClass =
            p.leasing_type === "LTR" ? "tag-ltr" : p.leasing_type === "STR" ? "tag-str" : "tag-hybrid";

          return (
            <button
              key={p.id}
              onClick={() => navigate(`/properties/${p.id}`)}
              data-testid={`prop-card-${p.id}`}
              className="card card-hover overflow-hidden text-left flex flex-col group"
            >
              {/* Card Photo Header */}
              <div className="aspect-[16/9] bg-gradient-to-br from-[#1C3A5E] to-[#0D9488] relative overflow-hidden">
                {p.image_url ? (
                  <img
                    src={p.image_url}
                    alt={p.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/30 font-black text-5xl select-none">
                    {getInitials(p.name)}
                  </div>
                )}
                <div className="absolute top-3 left-3 flex items-center gap-1.5">
                  <span className={`tag ${badgeClass} backdrop-blur-md`}>
                    {p.leasing_type}
                  </span>
                  {p.entity_name && (
                    <span className="tag bg-black/60 text-white backdrop-blur-md">
                      {p.entity_name}
                    </span>
                  )}
                </div>
              </div>

              {/* Details Body */}
              <div className="p-5 flex-1 flex flex-col justify-between">
                <div>
                  <h3 className="font-bold text-lg tracking-tight truncate">{p.name}</h3>
                  <div className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400 mt-1 truncate font-medium">
                    <MapPin size={13} className="shrink-0 text-teal-500" />
                    <span className="truncate">{p.address || "Address unlisted"}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 mt-4 pt-4 border-t border-[var(--border)] text-xs">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-600 dark:text-slate-400 font-semibold">
                      {p.leasing_type === "STR" ? "Nightly Rate" : "Monthly Rent"}
                    </div>
                    <div className="text-base font-bold tabular-nums text-teal-600 dark:text-teal-400 mt-0.5">
                      {formatCurrency(p.leasing_type === "STR" ? p.nightly_rate : p.monthly_rent)}
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-slate-600 dark:text-slate-400 font-semibold">
                      Mortgage Pmt
                    </div>
                    <div className="text-sm font-semibold tabular-nums mt-0.5 text-slate-700 dark:text-slate-300">
                      {formatCurrency(p.monthly_mortgage_payment)}/mo
                    </div>
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
        </>
      )}
    </Layout>
  );
}

export function Field({ label, value, onChange, type = "text", required, testId, placeholder, helpTopic, helpText }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="label mb-0">{label}</label>
        {(helpTopic || helpText) && <HelpTip topic={helpTopic} text={helpText} />}
      </div>
      <input
        type={type}
        value={value ?? ""}
        placeholder={placeholder ?? (type === "number" ? "0" : undefined)}
        onChange={(e) => onChange(e.target.value)}
        onFocus={(e) => {
          if (type === "number" && (value === 0 || value === "0")) {
            onChange("");
          } else {
            e.target.select?.();
          }
        }}
        onClick={(e) => {
          if (type === "number" && (value === 0 || value === "0")) {
            onChange("");
          }
        }}
        required={required}
        data-testid={testId}
        className="lapen-input"
        step={type === "number" ? "any" : undefined}
      />
    </div>
  );
}

export function SelectField({ label, value, onChange, options = [], testId, helpTopic, helpText }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="label mb-0">{label}</label>
        {(helpTopic || helpText) && <HelpTip topic={helpTopic} text={helpText} />}
      </div>
      <select className="lapen-input" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
        {(options || []).map((o) => {
          const optVal = typeof o === "object" && o !== null ? o.value : o;
          const optLabel = typeof o === "object" && o !== null ? o.label : o;
          return (
            <option key={String(optVal ?? "")} value={optVal ?? ""}>
              {optLabel}
            </option>
          );
        })}
      </select>
    </div>
  );
}

